import { Alignment, ArrayUtil, LeftOrRightAlignment } from "@mcleod/core";
import { ChangeEvent } from "../../events/ChangeEvent";
import { Checkbox } from "../checkbox/Checkbox";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { SelectCheckboxHeadingTableCell } from "./SelectCheckboxHeadingTableCell";
import { SelectCheckboxTableCell } from "./SelectCheckboxTableCell";
import { SelectionType } from "../../base/SelectionMode";
import { Table } from "./Table";
import { TableColumn } from "./TableColumn";
import { TableRow } from "./TableRow";
import { TableRowDisplayEvent } from "../../events/TableRowDisplayEvent";
import { TableSelectionEvent } from "../../events/TableSelectionEvent";

const log = DesignableObjectLogManager.getLogger("components.table.SelectCheckboxHandler");

export class SelectCheckboxHandler {
    private column: TableColumn;
    private columnHeadingCell: SelectCheckboxHeadingTableCell;
    private table: Table;

    constructor(table: Table) {
        this.table = table;
        this.createColumn();
        this.addListeners();
    }

    get columnAlignemnt(): LeftOrRightAlignment {
        return this.table.selectCheckboxAlignment;
    }

    private get selectAllCheckbox(): Checkbox {
        return this.columnHeadingCell.checkbox;
    }

    private createColumn() {
        this.columnHeadingCell = new SelectCheckboxHeadingTableCell({
            id: "columnHeaderCheckboxes",
            table: this.table,
        });
        this.columnHeadingCell.fixedWidth = 50;

        this.column = new TableColumn({
            headingDef: this.columnHeadingCell,
            cellDef: {
                cellProps: {
                    table: this.table
                },
                def: { type: "selectcheckboxcell" }
            }
        });
    }

    syncColumnPlacement() {
        const shouldBeFirst = this.columnAlignemnt === Alignment.LEFT;
        if (!this.table.columns.includes(this.column)) {
            // Add column
            this.addColumnToTable(shouldBeFirst);
        } else {
            // Move column if necessary
            if (shouldBeFirst && !ArrayUtil.isFirstElement(this.table.columns, this.column)) {
                this.table.moveColumn(this.column.index, 0);
            }
            else if (!shouldBeFirst && !ArrayUtil.isLastElement(this.table.columns, this.column)) {
                this.table.moveColumn(this.column.index, this.table.columns.length - 1);
            }
        }
    }

    private addColumnToTable(shouldBeFirst: boolean) {
        this.table.addColumn(this.column, false, true);
        let populatedRows: TableRow[] = [];

        // The designer row cells are already handled by addColumn()
        if (this.table._designer == null) {
            populatedRows = this.table.allRows?.filter(row => row.populatedDOM === true) ?? [];
        }

        if (!ArrayUtil.isEmptyArray(populatedRows)) {
            Promise.all(populatedRows.map(row => row.populateDOMCell(this.column)))
            .finally(() => {
                if (shouldBeFirst) {
                    this.table.moveColumn(this.column.index, 0);
                }
            });
        } else if(shouldBeFirst) {
            this.table.moveColumn(this.column.index, 0);
        }
    }

    removeColumn() {
        this.table.removeColumn(this.column);
    }

    private addListeners() {
        this.table.addSelectionListener((event: TableSelectionEvent) => {
            this.syncAllCheckboxes(event);
        });
        this.table.addRowDisplayListener((event: TableRowDisplayEvent) => {
            const tableRow = event.getTableRow();
            const checkbox = this.getCheckboxInRow(tableRow);
            if (checkbox != null)
                checkbox.checked = this.table.selectedRows.includes(tableRow);
            this.syncSelectAllCheckbox();
        });
        this.table.addDataDisplayListener(this.syncSelectAllCheckbox);
        this.table.addContentsChangedListener(this.syncSelectAllCheckbox);
        this.table.addFilterChangedListener(this.syncSelectAllCheckbox);
    }

    syncAllCheckboxes(event?: TableSelectionEvent) {
        const selectedRows = this.table.selectedRows;
        this.table.rows.forEach(tableRow => {
            const checkbox = this.getCheckboxInRow(tableRow);
            if (checkbox != null) {
                if (selectedRows.includes(tableRow) && this.isRowDisabled(tableRow)) {
                    checkbox.checked = false;
                    this.table.selectRow(tableRow, SelectionType.DISTINCT, event.domEvent);
                } else
                    checkbox.checked = selectedRows.includes(tableRow);
            }
        });
        this.syncSelectAllCheckbox();
    }

    checkboxSelectAllOnChange = (event: ChangeEvent) => {
        log.debug(this, "Select All Checkbox changed, event=%o", event);
        if (event.userInitiatedChange !== true || this.table._designer != null) {
            log.debug(this, "Disregarding, either in designer or not a user-initiated change");
            return;
        }
        if (this.table.rows.every(tableRow => tableRow.selected || this.isRowDisabled(tableRow))) {
            log.debug(this, "Unselecting all rows");
            this.table.selectedRows = [];
        }
        else {
            this.table.selectedRows = this.table.rows.filter(tableRow => !this.isRowDisabled(tableRow));
            log.debug(this, "Selected all enabled rows: %o", this.table.selectedRows);
        }
        this.syncSelectAllCheckbox();
    }

    private syncSelectAllCheckbox = () => {
        if (this.table.rows.length === 0) {
            log.debug(this, "Unchecking and disabling Select All Checkbox, table is empty");
            this.selectAllCheckbox.checked = false;
            this.selectAllCheckbox.enabled = false;
            return;
        }
        log.debug(this, "Enabling Select All Checkbox");
        this.selectAllCheckbox.enabled = true;

        // Only set allowIndeterminate here, when we know we might need it.  This allows normal toggling of the Select
        // All Checkbox when the user interacts with it.  The user should never be able to put the checkbox into the
        // indeterminate/undefined state...that should only be possible based on the user changing individual
        // row selections.
        this.selectAllCheckbox.allowIndeterminate = false;
        if (this.table.rows.every(tableRow => tableRow.selected || this.isRowDisabled(tableRow))) {
            log.debug(this, "Checking Select All Checkbox");
            this.selectAllCheckbox.checked = true;
        } else if (this.table.rows.every(tableRow => !tableRow.selected)) {
            log.debug(this, "Unchecking Select All Checkbox");
            this.selectAllCheckbox.checked = false;
        } else {
            log.debug(this, "Setting Select All Checkbox to indeterminate, only some rows are selected");
            this.selectAllCheckbox.allowIndeterminate = true;
            this.selectAllCheckbox.checked = undefined;
        }
    }

    public getCheckboxInRow(tableRow: TableRow): Checkbox {
        const cells = tableRow.cells?.filter(cell => {
            return cell instanceof SelectCheckboxTableCell || cell instanceof SelectCheckboxHeadingTableCell;
        });
        return ArrayUtil.getFirstElement(cells)?.checkbox;
    }

    private isRowDisabled(tableRow: TableRow): boolean {
        const checkbox = this.getCheckboxInRow(tableRow);
        const result = tableRow?.enabled === false || checkbox?.enabled === false;
        log.debug(this, "Testing if is TableRow disabled.  Row=%o, Is Disabled=%o", tableRow, result);
        return result;
    }
}
